import type { Config } from "./types.ts";
import type { TFunction } from "i18next";
import { unique } from "@exonn/utils";

function addToRuleHelpText(ruleHelpText: string, newRule: string) {
  return `${ruleHelpText ? ruleHelpText + ", " : ""}${newRule}`;
}

export function buildRuleHelpText({
  validationRules = {},
  maxItems,
  fileTypeHelpText,
  t,
}: {
  validationRules?: Partial<Config["validationRules"]>;
  maxItems?: number;
  fileTypeHelpText?: string;
  t: TFunction;
}): string | undefined {
  const fileTypeRules = validationRules.accept;
  const fileSizeRules = {
    min: validationRules.minSizeInKB,
    max: validationRules.maxSizeInKB,
  };

  let ruleHelpText = "";
  if (maxItems) {
    ruleHelpText = addToRuleHelpText(
      ruleHelpText,
      t("maxFileRule", {
        count: maxItems,
      }),
    );
  }

  if (fileTypeRules && !fileTypeHelpText) {
    const cleanFileTypes = fileTypeRules.reduce((acc, rule, i) => {
      return [
        ...acc,
        cleanRule({
          t,
          rule,
        }),
      ];
    }, [] as string[]);

    const uniqueRules = unique(cleanFileTypes);

    if (uniqueRules.length) {
      ruleHelpText = addToRuleHelpText(
        ruleHelpText,
        unique(cleanFileTypes).join(", "),
      );
    }
  }

  if (fileTypeHelpText) {
    ruleHelpText = addToRuleHelpText(ruleHelpText, fileTypeHelpText);
  }

  if (fileSizeRules.min) {
    const minSizeString =
      fileSizeRules.min > 1024
        ? (fileSizeRules.min / 1024).toFixed(2) + "MB"
        : fileSizeRules.min + "KB";
    ruleHelpText = addToRuleHelpText(
      ruleHelpText,
      t("minFileSizeRule", {
        size: minSizeString,
      }),
    );
  }

  if (fileSizeRules.max) {
    const maxSizeString =
      fileSizeRules.max > 1024
        ? (fileSizeRules.max / 1024).toFixed(2) + "MB"
        : fileSizeRules.max + "KB";
    ruleHelpText = addToRuleHelpText(
      ruleHelpText,
      t("maxFileSizeRule", {
        size: maxSizeString,
      }),
    );
  }

  return ruleHelpText;
}

export const cleanRule = ({ t, rule }: { t: TFunction; rule: string }) => {
  const lowerCaseRule = rule.toLowerCase();

  if (lowerCaseRule === "image/*") {
    return t("anyImage");
  }

  if (lowerCaseRule === "video/*") {
    return t("anyVideo");
  }

  if (lowerCaseRule === "text/plain") {
    return "TXT";
  }

  if (lowerCaseRule === "text/csv") {
    return "CSV";
  }

  if (
    lowerCaseRule === "application/zip" ||
    lowerCaseRule === "application/x-zip-compressed"
  ) {
    return "ZIP";
  }

  if (
    lowerCaseRule.startsWith("image/") ||
    lowerCaseRule.startsWith("application/") ||
    lowerCaseRule.startsWith("video/")
  ) {
    return lowerCaseRule
      .replace("image/", "")
      .replace("application/", "")
      .replace("video/", "")
      .toUpperCase();
  }

  return lowerCaseRule;
};
